import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import { SMTM_URL } from "@polifonia/env";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import urlJoin from "url-join";

import { EMPTY_STRING_PLACEHOLDER } from "@/appConstant";
import { Button } from "@/components/Button";
import { NumberFormat } from "@/components/NumberFormat";
import { Typography } from "@/components/Typography";
import { SurveyCakeTopUpButton } from "@/features/billing/SurveyCakeTopUpButton";
import { useSmtmNavigation } from "@/features/billing/useSmtmNavigation";
import { trackGaEvent } from "@/features/ga/trackGaEvent";
import { interlude } from "@/interlude";
import { ThresholdSettingModal } from "@/pages/billing/ThresholdSettingModal";

const styles = {
  card: {
    border: `1px solid ${theme.colors.neutral020}`,
    borderRadius: "12px",
  },
  cardContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "24px",
    "&:last-child": {
      paddingBottom: "24px",
    },
  },
  balanceLabel: {
    textTransform: "uppercase",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

// Taiwan org by IANA timezone. Null (e.g. token not yet backfilled) counts as
// non-Taiwan on purpose, falling back to the universal SurveyCake link rather
// than risking a non-Taiwan org hitting the Taiwan-only SMTM flow.
const isTaiwanOrganization = (
  organization: { timezone: string | null } | null | undefined,
): boolean => organization?.timezone === "Asia/Taipei";

export const CostSummaryCard: React.FC = function CostSummaryCard() {
  const { t, i18n } = useTranslation();

  const [alertSettingModalOpen, setAlertSettingModalOpen] = useState(false);

  const openAlertSettingModal = useHandler(() =>
    setAlertSettingModalOpen(true),
  );
  const closeAlertSettingModal = useHandler(() =>
    setAlertSettingModalOpen(false),
  );

  const balanceQuery = interlude.balance.useGetBalance();
  const meQuery = interlude.auth.useGetMe();
  const navigateToSmtm = useSmtmNavigation();

  const org = meQuery.data?.organization;

  const smtmTopUpUrl = useMemo(
    () =>
      SMTM_URL && org?.uuid
        ? urlJoin(SMTM_URL, "/organizations", org.uuid, "/topup-form")
        : null,
    [org?.uuid],
  );

  return (
    <Card variant="outlined" sx={styles.card}>
      <CardContent sx={styles.cardContent}>
        <Stack gap="4px">
          <Box component="span" sx={styles.balanceLabel}>
            <Typography variant="note">
              {t("billing.availableBalance.label")}
            </Typography>
          </Box>
          <Typography variant="value">
            {balanceQuery.isPending ? (
              <CircularProgress size={24} />
            ) : (
              <>
                <NumberFormat
                  nullishValue={EMPTY_STRING_PLACEHOLDER}
                  value={balanceQuery.data?.availableBalance}
                />
              </>
            )}
          </Typography>
        </Stack>
        <Stack direction="row" gap="10px">
          <Button
            variant="secondary"
            onClick={openAlertSettingModal}
            disabled={!balanceQuery.data}
          >
            {t("billing.alertSetting")}
          </Button>
          {/* Taiwan orgs use the SMTM self-service top-up flow; others fall back
              to the legacy external deposit link (same as Billing V1). */}
          {isTaiwanOrganization(org) ? (
            <Button
              variant="primary"
              disabled={!smtmTopUpUrl}
              onClick={() => {
                trackGaEvent("billingMaacTopUpClick");
                if (smtmTopUpUrl) navigateToSmtm(smtmTopUpUrl);
              }}
            >
              {t("billing.deposit")}
            </Button>
          ) : (
            <SurveyCakeTopUpButton
              orgName={org?.name}
              orgUuid={org?.uuid}
              language={i18n.language}
            />
          )}
        </Stack>
      </CardContent>
      <ThresholdSettingModal
        open={alertSettingModalOpen}
        closeModal={closeAlertSettingModal}
      />
    </Card>
  );
};
